import { useState, useCallback, useContext, useMemo } from "react";
import { AnnotationMessage, Drawing, MessageType, Point } from "../types/annotation-types";
import { getColorForUser } from "../utils/color-utils";
import { UserJoinInformation } from "../../../types";
import { MeetingInfoContext } from "../../../context";

interface AnnotationStateProps {
  userId: string
  userJoinInfo: UserJoinInformation
  addToRequestPoints: (sender: string) => void
}

const createStartPState = (userId: string, color: string): Record<string, Drawing> => {
  return {
    [userId]: {
      color,
      points: [],
      userMouse: null
    }
  }
}

export const useAnnotationState = ({
  userId,
  userJoinInfo,
  addToRequestPoints,
}: AnnotationStateProps) => {
  const [defaultColor] = useState<string>(getColorForUser(userId, userJoinInfo.joinInfo, userJoinInfo.joinNumber))
  const [pState, setPState] = useState<Record<string, Drawing>>(createStartPState(userId, defaultColor));
  const [lineIndex, setLineIndex] = useState<number>(-1);

  const {
    setShowAnnotation,
    annotationUrlIndex,
    setAnnotationUrlIndex,
  } = useContext(MeetingInfoContext);

  const { canReset, color } = useMemo(() => {
    let canReset = false;
    let color = defaultColor
    if (pState[userId]) {
      canReset = pState[userId].points.length > 0;
      color = pState[userId].color;
    }

    return {
      canReset,
      color
    }

  }, [pState, userId, defaultColor])

  const onMessage = (message: AnnotationMessage) => {
    switch (message.type) {
      case MessageType.Annotation:
        addAnnotationPoints(message)
        break
      case MessageType.Clear:
        clearUserAnnotations(message)
        break
      case MessageType.Undo:
        undoLastLine(message)
        break
      case MessageType.RequestPoints:
        clearUserAnnotations(message)
        addToRequestPoints(message.sender)
        break
      case MessageType.SendPoints:
        handleReceiveSendPointsMessage(message)
        break
      case MessageType.Color:
        handleReceiveColorMessage(message)
        break
      case MessageType.Active:
        handleReceiveActiveMessage(message)
        break
      case MessageType.MouseUpdate:
        handleReceiveMouseUpdateMessage(message)
        break
      default:
        console.warn("~~ivai - Received unexpected message type", message.type)
    }
  }

  const setColor = useCallback((color: string, targetId: string) => {
    setPState((prevPState) => {
      const updated = { ...prevPState };

      if (!updated[targetId]) {
        updated[targetId] = { points: [], color, userMouse: null };
      } else {
        updated[targetId].color = color;
      }

      return updated;
    });
  }, []);

  const addAnnotationPoints = useCallback((
    message: AnnotationMessage,
  ) => {
    const { sender, points, color: c, lineIndex, startIndex } = message;

    const color = c ? c : getColorForUser(sender, userJoinInfo.joinInfo)

    if (points == undefined || lineIndex == undefined || startIndex == undefined) {
      console.error(
        "~~ivai - Received annotation message with no points",
        message,
      );
      return;
    }

    const typedPoints = points as Point[];

    setPState((prevPState) => {
      const updated = { ...prevPState };

      // ensure the user state exists
      if (!updated[sender]) {
        updated[sender] = { points: [typedPoints], color, userMouse: null };
      } else {
        updated[sender].color = color;
      }

      // ensure the line exists
      for (let i = updated[sender].points.length - 1; i < lineIndex; i++) {
        updated[sender].points.push([]);
      }

      // update points if points already exist
      if (startIndex < updated[sender].points[lineIndex].length) {
        let currentIndex = 0;

        for (
          let i = startIndex;
          i < updated[sender].points[lineIndex].length;
          i++
        ) {
          updated[sender].points[lineIndex][i] = typedPoints[currentIndex];
          currentIndex++;
        }

        if (currentIndex < typedPoints.length) {
          updated[sender].points[lineIndex].push(
            ...typedPoints.slice(currentIndex)
          );
        }

        return updated;
      }

      // add placeholder points if points are missing
      if (startIndex > updated[sender].points[lineIndex].length) {

        let previousPoint: Point | null = null;
        if (updated[sender].points[lineIndex].length > 0) {
          // previous point is the last point in the line
          previousPoint =
            updated[sender].points[lineIndex][
            updated[sender].points[lineIndex].length - 1
            ];
        } else if (updated[sender].userMouse) {
          // previous point is the user's mouse position
          previousPoint = updated[sender].userMouse;
        }

        // we can't add points if we don't have a previous point
        if (previousPoint) {
          const length = updated[sender].points[lineIndex].length
          for (let i = length; i < startIndex; i++) {
            updated[sender].points[lineIndex].push(previousPoint);
          }
        }
      }

      // add the new points
      updated[sender].points[lineIndex].push(...typedPoints);

      return updated;
    });
  }, []);

  const handleReceiveColorMessage = useCallback(
    (message: AnnotationMessage) => {
      const { color, sender } = message

      if (!color) {
        console.error("No colour in change colour message!")
        return
      }

      setColor(color, sender);
    },
    [setColor]
  );

  const clearUserAnnotations = useCallback((message: AnnotationMessage) => {
    setPState((prevState) => {
      const newState = { ...prevState };

      if (newState[message.sender]) {
        newState[message.sender].points = [];

        if (message.color) {
          newState[message.sender].color = message.color;
        }
      } else if (message.color) {
        newState[message.sender] = { points: [], color: message.color, userMouse: null };
      }

      return newState;
    });
  }, []);

  const undoLastLine = useCallback((message: AnnotationMessage) => {

    const { sender } = message

    setPState((prevState) => {
      const newState = { ...prevState };

      if (newState[sender] && newState[sender].points.length > 0) {
        newState[sender].points.pop();
      }

      return newState;
    });
  }, []);

  const updateUserMouse = useCallback((sender: string, x: number, y: number, fallbackColor: string) => {
    setPState((prevPState) => {
      const updated = { ...prevPState };

      if (!updated[sender]) {
        console.log("~~ivai - No user state found for", sender);
        updated[sender] = { points: [], color: fallbackColor, userMouse: { x, y } };
      } else {
        updated[sender].userMouse = { x, y };
      }

      return updated;
    });
  }, []);

  const addPointToUser = useCallback((userId: string, x: number, y: number, color: string, index: number) => {
    setPState((prevPState) => {
      const updated = { ...prevPState };

      if (!updated[userId]) {
        updated[userId] = { points: [], color, userMouse: null };
      }

      for (let i = updated[userId].points.length - 1; i < index; i++) {
        updated[userId].points.push([]);
      }

      updated[userId].points[index].push({ x, y });

      return updated;
    });
  }, []);

  const setUserPoints = useCallback((sender: string, points: Drawing) => {
    setPState((prevState) => {
      const updated = { ...prevState };
      updated[sender] = points;
      return updated;
    });
  }, []);

  // Message handlers
  const handleReceiveActiveMessage = useCallback(
    (message: AnnotationMessage) => {
      const { sender, active, url, color } = message
      setActiveState(active, url);
      if (color) {
        setColor(color, sender);
      }
    },
    [setColor]
  );

  const handleReceiveMouseUpdateMessage = useCallback(
    (message: AnnotationMessage) => {

      const { x, y, sender } = message

      if (!x || !y) {
        console.error("Missing values in mouse message!", message)
        return
      }

      let color = pState[sender]?.color
      if (!color) {
        color = getColorForUser(sender, userJoinInfo.joinInfo)
      }

      updateUserMouse(sender, x, y, color)
    },
    [updateUserMouse, pState]
  );

  const clearAllState = useCallback(() => {
    setPState((previousState) =>
      Object.fromEntries(
        Object.entries(previousState).map(([userId, drawing]) => [
          userId,
          {
            ...drawing,
            points: [],
            userMouse: null
          },
        ]),
      ),
    );
  }, []);

  const setActiveState = useCallback(
    (active: any, url: any) => {
      const isActive = active === true || active === "true";

      if (isActive) {
        if (url === annotationUrlIndex) {
          console.log("~~ivai - Received active message with same url", url);
          return;
        }

        if (url === null || url === undefined) {
          console.warn("~~ivai - Received active message with no url");
          return;
        }
      }

      if (!isActive) {
        setAnnotationUrlIndex(null);
        setShowAnnotation(isActive);
        clearAllState();
        return;
      }

      setAnnotationUrlIndex(url);
      setShowAnnotation(isActive);
    },
    [
      annotationUrlIndex,
      setAnnotationUrlIndex,
      setShowAnnotation,
      clearAllState,
    ]
  );

  const handleReceiveSendPointsMessage = useCallback(
    (message: AnnotationMessage) => {
      const { drawing, url, active, sender } = message

      if (active === undefined) {
        console.error("~~ivai - missing active in send receive points message!", message)
        return
      }

      if (active === false || `${active}` === "false") {
        setActiveState(false, null)
        return
      }

      if (drawing === undefined) {
        console.error("~~ivai - missing drawing in send receive points message!", message)
        return
      }

      if (url === undefined) {
        console.error("~~ivai - missing url in send receive points message!", message)
        return
      }

      setUserPoints(sender, drawing)
      setActiveState(active, url)
      console.log("~~ivai - receiving state from user", sender, drawing, url)
    },
    [setUserPoints, setActiveState]
  );

  return {
    pState,
    lineIndex,
    color,
    canReset,
    setLineIndex,
    setColor,
    addAnnotationPoints,
    clearUserAnnotations,
    undoLastLine,
    updateUserMouse,
    addPointToUser,
    setUserPoints,
    clearAllState,
    onMessage,
  };
};
